import React, { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, IconButton, useTheme, useMediaQuery,
  Grid, InputAdornment, Chip, Checkbox, Slide,
  List, ListItem, ListItemIcon, ListItemText,
  Paper, ClickAwayListener, GlobalStyles, TextField, Alert, Collapse,
} from '@mui/material';
import CloseIcon            from '@mui/icons-material/Close';
import FilterListIcon       from '@mui/icons-material/FilterList';
import SearchIcon           from '@mui/icons-material/Search';
import ClearIcon            from '@mui/icons-material/Clear';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon         from '@mui/icons-material/CheckBox';
import UpdateIcon           from '@mui/icons-material/Update';
import GroupIcon            from '@mui/icons-material/Group';
import InfoIcon             from '@mui/icons-material/Info';
import { useDispatch, useSelector }         from 'react-redux';
import { LocalizationProvider }             from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment }                    from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker }                       from '@mui/x-date-pickers/DatePicker';
import moment                               from 'moment';

import { GetDevelopersData }                         from '../Redux/Actions/DevelopersAction';
import { GetAllCampainData }                         from '../Redux/Actions/Campaign_Actions';
import { GetAllchannalData }                         from '../Redux/Actions/ChannalActions';
import { GetallProjectsdata }                        from '../Redux/Actions/Projects_Action';
import { GetAll_stage_Data }                         from '../Redux/Actions/Stage_Action';
import { Get_All_Sales_By_TeamLeader_Email_Action }  from '../Redux/Actions/SalesAction';

/* ─── Tokens ─────────────────────────────────────────────────────── */
const ACCENT  = '#0D47A1';
const WARNING = '#F59E0B';

const SlideTransition = React.forwardRef((p, r) => <Slide direction="left" ref={r} {...p} />);
const icon        = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/* ─── safe array helper ──────────────────────────────────────────── */
const safeArray = (value) => {
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value))       return value;
  return [];
};

/* ─── StableMenu ─────────────────────────────────────────────────── */
const StableMenu = ({
  open, options, selected, searchValue, onSearchChange,
  onToggle, onSelectAll, onClose,
  anchorPosition, mode, textSecondary, inputBg, bg, border,
  getId, getName,
}) => {
  const listRef       = useRef(null);
  const scrollTopRef  = useRef(0);
  const searchRef     = useRef(null);
  const textPrimary   = mode === 'dark' ? '#fff' : '#1a1a2e';

  const safeOptions  = Array.isArray(options)  ? options  : [];
  const safeSelected = Array.isArray(selected) ? selected : [];

  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = scrollTopRef.current;
  });

  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => { const i = searchRef.current?.querySelector('input'); i?.focus(); }, 50);
    }
  }, [open]);

  const filtered = safeOptions.filter(opt =>
    getName(opt).toLowerCase().includes((searchValue || '').toLowerCase()) ||
    safeSelected.some(s => getId(s) === getId(opt))
  );

  if (!open) return null;

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Paper sx={{
        position: 'fixed',
        top: anchorPosition.top, left: anchorPosition.left, width: anchorPosition.width,
        maxHeight: 360, bgcolor: bg, border: `1px solid ${border}`,
        borderRadius: '12px', overflow: 'hidden', zIndex: 999999,
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
      }}>
        {/* Search */}
        <Box ref={searchRef} sx={{ p: 1.5, borderBottom: `1px solid ${border}` }}>
          <TextField fullWidth size="small" placeholder="Search…" value={searchValue || ''}
            onChange={e => onSearchChange(e.target.value)} autoFocus
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: textSecondary }} /></InputAdornment>,
              endAdornment: searchValue && <IconButton size="small" onClick={() => onSearchChange('')}><ClearIcon sx={{ fontSize: 16 }} /></IconButton>,
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: inputBg, '& input': { color: textPrimary } } }}
          />
        </Box>

        {/* Select All */}
        <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${border}` }}>
          <Button size="small" onClick={() => onSelectAll(safeOptions)}
            sx={{ textTransform: 'none', color: ACCENT, fontSize: 12, fontWeight: 500 }}>
            {safeSelected.length === safeOptions.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>

        {/* List */}
        <List ref={listRef} onScroll={() => { scrollTopRef.current = listRef.current?.scrollTop || 0; }}
          sx={{ maxHeight: 260, overflow: 'auto', p: 0 }}>
          {filtered.length > 0 ? filtered.map(opt => {
            const sel = safeSelected.some(s => getId(s) === getId(opt));
            return (
              <ListItem key={getId(opt)} onClick={() => onToggle(opt)}
                sx={{ cursor: 'pointer', py: 1.5, '&:hover': { bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f5f5f5' } }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox checked={sel} icon={icon} checkedIcon={checkedIcon}
                    sx={{ p: 0, color: textSecondary, '&.Mui-checked': { color: ACCENT } }} />
                </ListItemIcon>
                <ListItemText primary={getName(opt)}
                  primaryTypographyProps={{ fontSize: 13, fontWeight: sel ? 500 : 400, color: textPrimary }} />
              </ListItem>
            );
          }) : (
            <ListItem disabled sx={{ justifyContent: 'center', py: 3 }}>
              <Typography variant="body2" color={textSecondary}>No options found</Typography>
            </ListItem>
          )}
        </List>
      </Paper>
    </ClickAwayListener>
  );
};

/* ─── CustomDatePicker ───────────────────────────────────────────── */
const CustomDatePicker = ({ label, value, onChange, color = 'primary', ...props }) => {
  const mode = useTheme().palette.mode;
  const c = color === 'warning'
    ? (mode === 'dark' ? '#ff9800' : WARNING)
    : (mode === 'dark' ? '#90caf9' : ACCENT);

  return (
    <DatePicker label={label} value={value} onChange={onChange} views={['year', 'month', 'day']}
      slotProps={{
        textField: {
          fullWidth: true, size: 'small',
          sx: {
            bgcolor: mode === 'dark' ? '#2a2a42' : '#f9fafb', borderRadius: '10px',
            '& .MuiInputLabel-root': { color: mode === 'dark' ? '#888' : '#6b7280', fontSize: 13, '&.Mui-focused': { color: c } },
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              '& fieldset': { borderColor: mode === 'dark' ? '#333' : '#e5e7eb' },
              '&:hover fieldset': { borderColor: c },
              '&.Mui-focused fieldset': { borderColor: c, borderWidth: 1 },
            },
          },
        },
        popper: {
          sx: {
            zIndex: '9999999 !important',
            '& .MuiPaper-root': { zIndex: '9999999 !important', marginTop: '8px', borderRadius: '12px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
            '& .MuiPickersDay-root': { fontSize: 13, width: 36, height: 36, borderRadius: 10 },
          },
          disablePortal: false, placement: 'bottom-start',
          modifiers: [
            { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport', altBoundary: true, padding: 8 } },
            { name: 'flip', enabled: true, options: { fallbackPlacements: ['bottom-start', 'bottom-end', 'top-start'] } },
          ],
        },
      }}
      {...props}
    />
  );
};

/* ════════════════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════════════ */
export const TeamLeaderQuickFilterModal = ({ open, onClose, onApplyFilters, initialFilters }) => {
  const theme         = useTheme();
  const mode          = theme.palette.mode;
  const isSmall       = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch      = useDispatch();

  /* theme tokens */
  const bg          = mode === 'dark' ? '#1a1a2e' : '#ffffff';
  const textPrimary = mode === 'dark' ? '#fff'    : '#1a1a2e';
  const textSec     = mode === 'dark' ? '#888'    : '#6b7280';
  const border      = mode === 'dark' ? '#333'    : '#e5e7eb';
  const inputBg     = mode === 'dark' ? '#2a2a42' : '#f9fafb';

  /* user */
  const [userlogin, setUserlogin] = useState(null);
  useEffect(() => { const u = localStorage.getItem('user'); if (u) setUserlogin(JSON.parse(u)); }, []);

  const isTeamLeader  = userlogin?.data?.role === 'Team Leader';
  const currentUserId = userlogin?.data?._id;
  const userEmail     = userlogin?.data?.email;

  /* filters state */
  const EMPTY = {
    stage: [], project: [], developer: [], chanel: [], campaign: [], sales: [],
    createdAt: { from: null, to: null },
    stagedateupdated: { from: null, to: null },
  };
  const [filters, setFilters] = useState(initialFilters || EMPTY);

  /* menu state */
  const [openMenu,       setOpenMenu]       = useState(null);
  const [searchValue,    setSearchValue]    = useState('');
  const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0, width: 0 });

  /* refs */
  const fieldRefs = {
    stage: useRef(null), project: useRef(null), developer: useRef(null),
    chanel: useRef(null), campaign: useRef(null), sales: useRef(null),
  };

  /* redux — use safeArray everywhere so we never get undefined */
  const reschannel    = useSelector(s => s.AllChannalRoot?.channal);
  const resproject    = useSelector(s => s.AllProjects?.Projects);
  const resstages     = useSelector(s => s.Allstages?.stage);
  const resdevelopers = useSelector(s => s.AllDevelopers?.Developers);
  const rescampaigns  = useSelector(s => s.AllCampaign?.campaign);
  const resGetAllsales = useSelector(s => s.AllSales?.salesforteamleaders);

  const channelarray    = safeArray(reschannel);
  const projectarray    = safeArray(resproject);
  const stagesarray     = safeArray(resstages);
  const developersarray = safeArray(resdevelopers);
  const campaignsarray  = safeArray(rescampaigns);

  const salesArray = useMemo(() => safeArray(resGetAllsales), [resGetAllsales]);

  const teamLeaderSales = useMemo(() => {
    if (!salesArray.length || !currentUserId) return [];
    return salesArray.filter(s => s?.teamleader?._id === currentUserId || s?.teamleader === currentUserId);
  }, [salesArray, currentUserId]);

  const hasTeamSales = teamLeaderSales.length > 0;

  /* helpers */
  const getId  = useCallback(item => item?._id || item?.id || item?.name || item?.CampainName || JSON.stringify(item), []);
  const getName = useCallback(item => {
    if (!item) return 'Unknown';
    if (typeof item === 'string') return item;
    return item?.name || item?.username || item?.email || item?.CampainName || 'Unknown';
  }, []);

  /* fetch */
  useEffect(() => {
    if (!open) return;
    dispatch(GetAllchannalData());
    dispatch(GetallProjectsdata());
    dispatch(GetAll_stage_Data());
    dispatch(GetDevelopersData());
    dispatch(GetAllCampainData());
    if (isTeamLeader && userEmail) dispatch(Get_All_Sales_By_TeamLeader_Email_Action(userEmail));
  }, [open, isTeamLeader, userEmail, dispatch]);

  /* reset when initialFilters changes */
  useEffect(() => { if (initialFilters) setFilters(initialFilters); }, [initialFilters]);

  /* toggle */
  const handleToggle = useCallback((filterType, item) => {
    setFilters(prev => {
      const current = Array.isArray(prev[filterType]) ? prev[filterType] : [];
      return {
        ...prev,
        [filterType]: current.some(i => getId(i) === getId(item))
          ? current.filter(i => getId(i) !== getId(item))
          : [...current, item],
      };
    });
  }, [getId]);

  const handleSelectAll = useCallback((filterType, options) => {
    const safeOpts = Array.isArray(options) ? options : [];
    setFilters(prev => {
      const current = Array.isArray(prev[filterType]) ? prev[filterType] : [];
      return {
        ...prev,
        [filterType]: current.length === safeOpts.length ? [] : [...safeOpts],
      };
    });
  }, []);

  const handleDateChange = (type, field, value) => {
    setFilters(prev => ({ ...prev, [type]: { ...prev[type], [field]: value ? moment(value).format('YYYY-MM-DD') : null } }));
  };

  /* open / close menu */
  const handleOpenMenu = menuType => {
    const ref = fieldRefs[menuType];
    if (ref?.current) {
      const r = ref.current.getBoundingClientRect();
      setAnchorPosition({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX, width: r.width });
      setOpenMenu(menuType);
      setSearchValue('');
    }
  };
  const handleCloseMenu = () => { setOpenMenu(null); setSearchValue(''); };

  /* apply */
  const handleApply = () => {
    const q = new URLSearchParams();
    ['stage', 'project', 'developer', 'chanel', 'campaign'].forEach(f => {
      const arr = Array.isArray(filters[f]) ? filters[f] : [];
      arr.filter(Boolean).forEach(item => q.append(f, getId(item)));
    });
    const salesArr = Array.isArray(filters.sales) ? filters.sales : [];
    salesArr.filter(Boolean).forEach(s => q.append('sales', getId(s)));
    if (filters.createdAt?.from)        q.append('createdFrom',  filters.createdAt.from);
    if (filters.createdAt?.to)          q.append('createdTo',    filters.createdAt.to);
    if (filters.stagedateupdated?.from) q.append('stageDateFrom', filters.stagedateupdated.from);
    if (filters.stagedateupdated?.to)   q.append('stageDateTo',   filters.stagedateupdated.to);

    onApplyFilters(filters, q.toString());
    handleCloseMenu();
    onClose();
  };

  const handleClear = () => { setFilters(EMPTY); handleCloseMenu(); };

  /* options per menu */
  const getOptions = m => {
    switch (m) {
      case 'stage':     return stagesarray;
      case 'project':   return projectarray;
      case 'developer': return developersarray;
      case 'chanel':    return channelarray;
      case 'campaign':  return campaignsarray;
      case 'sales':     return teamLeaderSales;
      default: return [];
    }
  };

  /* has filters */
  const hasFilters =
    (filters.stage?.length  > 0) || (filters.project?.length   > 0) ||
    (filters.developer?.length > 0) || (filters.chanel?.length > 0) ||
    (filters.campaign?.length  > 0) || (filters.sales?.length  > 0) ||
    filters.createdAt?.from || filters.createdAt?.to ||
    filters.stagedateupdated?.from || filters.stagedateupdated?.to;

  /* ─── FilterField ─────────────────────────────────────────────── */
  const FilterField = ({ title, filterType, icon: Icon, placeholder, disabled }) => {
    const selected = Array.isArray(filters[filterType]) ? filters[filterType] : [];
    const isOpen   = openMenu === filterType;

    return (
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: textSec, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {Icon && <Icon sx={{ fontSize: 14, color: ACCENT }} />}
          {title}
          {filterType === 'sales' && isTeamLeader && (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: '#10B981' }}>
              ({teamLeaderSales.length} available)
            </Typography>
          )}
        </Typography>

        <Box
          ref={fieldRefs[filterType]}
          onClick={() => !disabled && (isOpen ? handleCloseMenu() : handleOpenMenu(filterType))}
          sx={{
            border: `1px solid ${isOpen && !disabled ? ACCENT : border}`,
            borderRadius: '10px', bgcolor: disabled ? `${inputBg}80` : inputBg,
            px: 2, py: 1.5, cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            opacity: disabled ? 0.6 : 1,
            transition: 'border-color 0.15s',
            '&:hover': { borderColor: disabled ? border : ACCENT },
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
            {selected.length > 0 ? (
              <>
                {selected.slice(0, 2).map((item, i) => (
                  <Chip key={i} label={getName(item)} size="small"
                    onDelete={e => { e.stopPropagation(); if (!disabled) handleToggle(filterType, item); }}
                    sx={{ height: 24, fontSize: 11, borderRadius: '6px', bgcolor: ACCENT, color: '#fff', '& .MuiChip-deleteIcon': { color: '#fff', fontSize: 14 } }} />
                ))}
                {selected.length > 2 && (
                  <Chip label={`+${selected.length - 2}`} size="small"
                    sx={{ height: 24, fontSize: 11, bgcolor: border, color: textSec }} />
                )}
              </>
            ) : (
              <Typography sx={{ fontSize: 13, color: textSec }}>
                {disabled ? 'No sales assigned to your team' : placeholder}
              </Typography>
            )}
          </Box>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0, marginLeft: 8 }}>
            <path d="M6 9L12 15L18 9" stroke={textSec} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Box>
      </Box>
    );
  };

  /* ─── Stage chips ──────────────────────────────────────────────── */
  const StageChip = ({ stage }) => {
    const sel = Array.isArray(filters.stage) && filters.stage.some(s => getId(s) === getId(stage));
    return (
      <Box onClick={() => handleToggle('stage', stage)}
        sx={{
          px: 2, py: 1, borderRadius: '20px', border: `1px solid ${border}`,
          bgcolor: sel ? ACCENT : 'transparent', cursor: 'pointer',
          transition: 'all 0.15s',
          '&:hover': { transform: 'translateY(-1px)', bgcolor: sel ? ACCENT : (mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f5f5f5') },
        }}>
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: sel ? '#fff' : textPrimary }}>
          {getName(stage)}
        </Typography>
      </Box>
    );
  };

  /* current menu */
  const currentOptions  = openMenu ? getOptions(openMenu) : [];
  const currentSelected = openMenu ? (Array.isArray(filters[openMenu]) ? filters[openMenu] : []) : [];

  /* ─── RENDER ──────────────────────────────────────────────────── */
  return (
    <>
      <GlobalStyles styles={{ '.MuiPickersPopper-root': { zIndex: 999999 }, '.MuiPickersPopper-paper': { zIndex: 999999 } }} />

      <Dialog
        open={open}
        onClose={() => { handleCloseMenu(); onClose(); }}
        fullScreen={isSmall}
        maxWidth="sm" fullWidth
        TransitionComponent={SlideTransition}
        disableEnforceFocus disableAutoFocus
        PaperProps={{
          sx: {
            borderRadius: isSmall ? 0 : '20px', bgcolor: bg,
            margin: isSmall ? 0 : 2,
            height: isSmall ? '100vh' : 'auto', maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          },
        }}
        sx={{ zIndex: 60000 }}
      >
        {/* Header */}
        <DialogTitle sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FilterListIcon sx={{ color: ACCENT, fontSize: 22 }} />
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: textPrimary, letterSpacing: '-0.01em' }}>
                Quick Filter
              </Typography>
              {isTeamLeader && (
                <Typography sx={{ fontSize: 11, color: textSec }}>
                  {hasTeamSales ? `${teamLeaderSales.length} sales in your team` : 'No sales assigned'}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={() => { handleCloseMenu(); onClose(); }} size="small">
            <CloseIcon sx={{ color: textSec }} />
          </IconButton>
        </DialogTitle>

        {/* Body */}
        <DialogContent sx={{ p: 3, overflow: 'auto' }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>

            {/* Sales — Team Leader only */}
            {isTeamLeader && (
              <FilterField
                title="My Team Sales"
                filterType="sales"
                icon={GroupIcon}
                placeholder="Select sales from your team…"
                disabled={!hasTeamSales}
              />
            )}

            {/* Stage chips */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: textSec, mb: 1 }}>Stage</Typography>
              {stagesarray.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {stagesarray.map(s => <StageChip key={getId(s)} stage={s} />)}
                </Box>
              ) : (
                <Box sx={{ border: `1px solid ${border}`, borderRadius: '10px', p: 2, textAlign: 'center', bgcolor: inputBg }}>
                  <Typography sx={{ fontSize: 13, color: textSec }}>Loading stages…</Typography>
                </Box>
              )}
            </Box>

            <FilterField title="Project"   filterType="project"   placeholder="Select Project…" />
            <FilterField title="Developer" filterType="developer" placeholder="Select Developer…" />
            <FilterField title="Channel"   filterType="chanel"    placeholder="Select Channel…" />
            <FilterField title="Campaign"  filterType="campaign"  placeholder="Select Campaign…" />

            {/* Creation Date */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: textSec, mb: 1 }}>Creation Date Range</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <CustomDatePicker label="From Date"
                    value={filters.createdAt?.from ? moment(filters.createdAt.from) : null}
                    onChange={d => handleDateChange('createdAt', 'from', d)} />
                </Grid>
                <Grid item xs={6}>
                  <CustomDatePicker label="To Date"
                    value={filters.createdAt?.to ? moment(filters.createdAt.to) : null}
                    onChange={d => handleDateChange('createdAt', 'to', d)} />
                </Grid>
              </Grid>
            </Box>

            {/* Stage Date */}
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <UpdateIcon sx={{ fontSize: 16, color: WARNING }} />
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: textSec }}>Stage Date Range</Typography>
              </Box>
              <Typography variant="caption" sx={{ color: textSec, fontSize: 10, mb: 1, display: 'block' }}>
                Filter by stage update date
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <CustomDatePicker label="From Date" color="warning"
                    value={filters.stagedateupdated?.from ? moment(filters.stagedateupdated.from) : null}
                    onChange={d => handleDateChange('stagedateupdated', 'from', d)} />
                </Grid>
                <Grid item xs={6}>
                  <CustomDatePicker label="To Date" color="warning"
                    value={filters.stagedateupdated?.to ? moment(filters.stagedateupdated.to) : null}
                    onChange={d => handleDateChange('stagedateupdated', 'to', d)} />
                </Grid>
              </Grid>
            </Box>

            {/* Summary */}
            {hasFilters && (
              <Box sx={{ mt: 3, p: 2, bgcolor: mode === 'dark' ? 'rgba(13,71,161,0.1)' : 'rgba(13,71,161,0.05)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textPrimary, mb: 1, fontSize: 12 }}>
                  Selected Filters:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {filters.sales?.length > 0      && <Chip label={`Sales: ${filters.sales.length}`}           size="small" sx={{ height: 24, fontSize: 11 }} />}
                  {filters.stage?.length > 0      && <Chip label={`Stage: ${filters.stage.length}`}           size="small" sx={{ height: 24, fontSize: 11 }} />}
                  {filters.project?.length > 0    && <Chip label={`Project: ${filters.project.length}`}       size="small" sx={{ height: 24, fontSize: 11 }} />}
                  {filters.developer?.length > 0  && <Chip label={`Developer: ${filters.developer.length}`}   size="small" sx={{ height: 24, fontSize: 11 }} />}
                  {filters.chanel?.length > 0     && <Chip label={`Channel: ${filters.chanel.length}`}        size="small" sx={{ height: 24, fontSize: 11 }} />}
                  {filters.campaign?.length > 0   && <Chip label={`Campaign: ${filters.campaign.length}`}     size="small" sx={{ height: 24, fontSize: 11 }} />}
                  {(filters.createdAt?.from || filters.createdAt?.to) && (
                    <Chip label="Creation Date Range" size="small" sx={{ height: 24, fontSize: 11 }} />
                  )}
                  {(filters.stagedateupdated?.from || filters.stagedateupdated?.to) && (
                    <Chip label="Stage Date Range" size="small" icon={<UpdateIcon />} sx={{ height: 24, fontSize: 11 }} />
                  )}
                </Box>
              </Box>
            )}

          </LocalizationProvider>
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${border}`, gap: 2 }}>
          <Button onClick={handleClear}
            sx={{ color: textSec, textTransform: 'none', fontWeight: 500, fontSize: 13, '&:hover': { bgcolor: 'transparent', color: ACCENT } }}>
            CLEAR ALL
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => { handleCloseMenu(); onClose(); }}
            sx={{ color: textSec, textTransform: 'none', fontWeight: 500, fontSize: 13, '&:hover': { bgcolor: 'transparent', color: ACCENT } }}>
            Cancel
          </Button>
          <Button onClick={handleApply} variant="contained"
            sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 600, px: 4, borderRadius: '10px', fontSize: 13, boxShadow: 'none', '&:hover': { bgcolor: '#0a3880', boxShadow: 'none' } }}>
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dropdown menu */}
      <StableMenu
        open={!!openMenu}
        options={currentOptions}
        selected={currentSelected}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onToggle={item => handleToggle(openMenu, item)}
        onSelectAll={opts => handleSelectAll(openMenu, opts)}
        onClose={handleCloseMenu}
        anchorPosition={anchorPosition}
        mode={mode}
        textSecondary={textSec}
        inputBg={inputBg}
        bg={bg}
        border={border}
        getId={getId}
        getName={getName}
      />
    </>
  );
};
